import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Text,
  Input,
  VStack,
  HStack,
  Grid,
  GridItem,
  Flex,
  NativeSelect,
  Spinner,
} from "@chakra-ui/react";

import DataTailorService from "./DataTailorService";

/**
 * Catalog-driven Data Tailor chain builder (decision D2: a single parameterized
 * process whose typed inputs are the chain_config fields). Builds a chain_config
 * object from form controls populated by the DT catalog, and offers Validate /
 * Estimate against the DTS gateway. Reports the built config up via onChange.
 */
export default function ChainBuilder({ value, onChange, inputProductCount = 1, collectionId = "" }) {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Field state
  const [preset, setPreset] = useState("");
  const [product, setProduct] = useState(value?.product || "");
  const [format, setFormat] = useState(value?.format || "");
  const [bands, setBands] = useState(value?.filter?.bands || []);
  const [projection, setProjection] = useState(value?.projection || "");
  const [aggregation, setAggregation] = useState(value?.aggregation || "");
  const [compression, setCompression] = useState(value?.compression || "");
  const [roiMode, setRoiMode] = useState("none");
  const [roiNamed, setRoiNamed] = useState("");
  const [roi, setRoi] = useState({ N: "", S: "", W: "", E: "" });

  // Validate / estimate result state
  const [validation, setValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    let ignore = false;
    DataTailorService.getCatalog()
      .then((c) => { if (!ignore) setCatalog(c.resources || {}); })
      .catch((e) => { if (!ignore) setCatalogError(e.message); });
    return () => { ignore = true; };
  }, []);

  const buildConfig = useCallback(() => {
    const cfg = {};
    if (product) cfg.product = product;
    if (format) cfg.format = format;
    if (bands.length) cfg.filter = { bands };
    if (projection) cfg.projection = projection;
    if (roiMode === "named" && roiNamed) {
      cfg.roi = roiNamed;
    } else if (roiMode === "custom" &&
      roi.N !== "" && roi.S !== "" && roi.W !== "" && roi.E !== "") {
      // DT inline ROI uses NSWE = [latMax, latMin, lonMin, lonMax].
      cfg.roi = { NSWE: [Number(roi.N), Number(roi.S), Number(roi.W), Number(roi.E)] };
    }
    if (aggregation) cfg.aggregation = aggregation;
    if (compression) cfg.compression = compression;
    return cfg;
  }, [product, format, bands, projection, roiMode, roiNamed, roi, aggregation, compression]);

  // Propagate the built config to the parent and reset stale results.
  useEffect(() => {
    const cfg = buildConfig();
    onChange?.(cfg);
    setValidation(null);
    setEstimate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildConfig]);

  const products = catalog?.products || [];
  const formats = catalog?.formats || [];
  const projections = catalog?.projections || [];
  const rois = catalog?.rois || [];
  const aggregations = catalog?.aggregations || [];
  const compressions = catalog?.compressions || [];
  const chains = catalog?.chains || [];

  const selectedProduct = products.find((p) => p.id === product);

  // Collection compatibility (pn_id from the DT catalog): hide product classes
  // registered for other collections so a mismatched pairing - which epct
  // rejects at run time - cannot be selected by accident. Unknown (no pn_id)
  // entries stay visible; "show all" restores the full list.
  const compatibilityOf = (p) => DataTailorService.isProductCompatible(p, collectionId);
  const visibleProducts = (!collectionId || showAllProducts)
    ? products
    : products.filter((p) => compatibilityOf(p) !== false || p.id === product);
  const hiddenCount = products.length - visibleProducts.length;
  const selectedCompatibility = selectedProduct ? compatibilityOf(selectedProduct) : null;
  const compatibleIds = collectionId
    ? DataTailorService.productsForCollection(products, collectionId).map((p) => p.id)
    : [];
  const availableBands = selectedProduct?.bands ? Object.entries(selectedProduct.bands) : [];

  const applyPreset = (chainId) => {
    setPreset(chainId);
    const chain = chains.find((c) => c.id === chainId);
    if (!chain) return;
    setProduct(chain.product || "");
    setFormat(chain.format || "");
    setBands(chain.filter?.bands || []);
    setProjection(chain.projection || "");
    setAggregation(chain.aggregation || "");
    setCompression(typeof chain.compression === "string" ? chain.compression : "");
    if (typeof chain.roi === "string") {
      setRoiMode("named");
      setRoiNamed(chain.roi);
    } else {
      setRoiMode("none");
      setRoiNamed("");
    }
  };

  const toggleBand = (bandId) => {
    setBands((prev) => prev.includes(bandId)
      ? prev.filter((b) => b !== bandId)
      : [...prev, bandId]);
  };

  const handleValidate = async () => {
    setIsValidating(true);
    setActionError(null);
    setValidation(null);
    try {
      setValidation(await DataTailorService.validateChain(buildConfig()));
    } catch (e) {
      setActionError(`Validation request failed: ${e.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleEstimate = async () => {
    setIsEstimating(true);
    setActionError(null);
    setEstimate(null);
    try {
      setEstimate(await DataTailorService.estimateCost({
        chainConfig: buildConfig(),
        nrInputProducts: Math.max(1, inputProductCount),
      }));
    } catch (e) {
      setActionError(`Estimate request failed: ${e.message}`);
    } finally {
      setIsEstimating(false);
    }
  };

  const labelFor = (item) => item.name ? `${item.id} — ${item.name}` : item.id;
  const yamlPreview = DataTailorService.chainConfigToYaml(buildConfig());
  const canRun = Boolean(product && format);

  if (catalogError) {
    return (
      <Text color="orange.600" fontSize="sm">
        Could not load the Data Tailor catalog ({catalogError}). Chain building is unavailable.
      </Text>
    );
  }

  if (!catalog) {
    return <HStack><Spinner size="sm" color="blue.500" /><Text fontSize="sm" color="gray.500">Loading catalog…</Text></HStack>;
  }

  return (
    <VStack align="stretch" gap={5}>
      {chains.length > 0 && (
        <Box>
          <Text mb={1} fontSize="sm" fontWeight="600">Start from a preset (optional)</Text>
          <NativeSelect.Root maxW="500px">
            <NativeSelect.Field value={preset} onChange={(e) => applyPreset(e.target.value)}>
              <option value="">No preset</option>
              {chains.map((c) => (
                <option key={c.id} value={c.id}>{labelFor(c)}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
      )}

      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
        <GridItem>
          <Text mb={1} fontSize="sm" fontWeight="600">Product *</Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={product} onChange={(e) => { setProduct(e.target.value); setBands([]); }}>
              <option value="">Select a product…</option>
              {visibleProducts.map((p) => <option key={p.id} value={p.id}>{labelFor(p)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {selectedCompatibility === false && (
            <Text mt={1} fontSize="xs" color="red.500">
              '{product}' is not registered for collection {collectionId} in the Data Tailor
              catalog - the job would fail the input check.
              {compatibleIds.length > 0 && ` Compatible: ${compatibleIds.join(", ")}`}
            </Text>
          )}
          {collectionId && hiddenCount > 0 && (
            <Text mt={1} fontSize="xs" color="gray.500" as="label" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showAllProducts}
                onChange={(e) => setShowAllProducts(e.target.checked)}
                style={{ marginRight: "6px" }}
              />
              Show all products ({hiddenCount} hidden as registered for other collections)
            </Text>
          )}
        </GridItem>

        <GridItem>
          <Text mb={1} fontSize="sm" fontWeight="600">Output format *</Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="">Select a format…</option>
              {formats.map((f) => <option key={f.id} value={f.id}>{labelFor(f)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </GridItem>
      </Grid>

      {availableBands.length > 0 && (
        <Box>
          <Text mb={2} fontSize="sm" fontWeight="600">Bands (filter, optional)</Text>
          <Flex gap={4} flexWrap="wrap">
            {availableBands.map(([bandId, meta]) => (
              <Flex key={bandId} align="center" gap={2}>
                <input
                  type="checkbox"
                  checked={bands.includes(bandId)}
                  onChange={() => toggleBand(bandId)}
                  style={{ accentColor: "#3182ce" }}
                />
                <Text fontSize="sm" color="gray.700">{bandId}{meta?.name ? ` (${meta.name})` : ""}</Text>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
        <GridItem>
          <Text mb={1} fontSize="sm" fontWeight="600">Projection (optional)</Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={projection} onChange={(e) => setProjection(e.target.value)}>
              <option value="">None</option>
              {projections.map((p) => <option key={p.id} value={p.id}>{labelFor(p)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </GridItem>
        <GridItem>
          <Text mb={1} fontSize="sm" fontWeight="600">Aggregation (optional)</Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={aggregation} onChange={(e) => setAggregation(e.target.value)}>
              <option value="">None</option>
              {aggregations.map((a) => <option key={a.id} value={a.id}>{labelFor(a)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </GridItem>
        <GridItem>
          <Text mb={1} fontSize="sm" fontWeight="600">Compression (optional)</Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={compression} onChange={(e) => setCompression(e.target.value)}>
              <option value="">None</option>
              {compressions.map((c) => <option key={c.id} value={c.id}>{labelFor(c)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </GridItem>
      </Grid>

      <Box>
        <Text mb={1} fontSize="sm" fontWeight="600">Region of interest (optional)</Text>
        <NativeSelect.Root maxW="300px" mb={3}>
          <NativeSelect.Field value={roiMode} onChange={(e) => setRoiMode(e.target.value)}>
            <option value="none">None</option>
            <option value="named">Named region</option>
            <option value="custom">Custom bounds</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        {roiMode === "named" && (
          <NativeSelect.Root maxW="300px">
            <NativeSelect.Field value={roiNamed} onChange={(e) => setRoiNamed(e.target.value)}>
              <option value="">Select a region…</option>
              {rois.map((r) => <option key={r.id} value={r.id}>{labelFor(r)}</option>)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        )}

        {roiMode === "custom" && (
          <Grid templateColumns="repeat(4, 1fr)" gap={3} maxW="500px">
            {["N", "S", "W", "E"].map((k) => (
              <GridItem key={k}>
                <Text mb={1} fontSize="xs" fontWeight="600">{k}</Text>
                <Input
                  type="number"
                  value={roi[k]}
                  onChange={(e) => setRoi((prev) => ({ ...prev, [k]: e.target.value }))}
                  size="sm"
                />
              </GridItem>
            ))}
          </Grid>
        )}
      </Box>

      <Box bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
        <Text mb={1} fontSize="xs" fontWeight="600" color="gray.600">chain_config preview</Text>
        <Text fontFamily="mono" fontSize="xs" color="gray.800" whiteSpace="pre">
          {yamlPreview || "# select a product and format to begin"}
        </Text>
      </Box>

      <HStack gap={3}>
        <Button onClick={handleValidate} disabled={!canRun || isValidating} variant="outline" size="sm">
          {isValidating ? "Validating…" : "Validate"}
        </Button>
        <Button onClick={handleEstimate} disabled={!canRun || isEstimating} variant="outline" size="sm">
          {isEstimating ? "Estimating…" : "Estimate"}
        </Button>
      </HStack>

      {actionError && <Text color="red.500" fontSize="sm">{actionError}</Text>}

      {validation && (
        <Box fontSize="sm">
          {validation.valid ? (
            <Text color="green.600" fontWeight="medium">Chain configuration is valid.</Text>
          ) : (
            <Box color="red.500">
              <Text fontWeight="medium">Chain configuration is not valid:</Text>
              <VStack align="stretch" gap={0} pl={3}>
                {(validation.errors || [validation.reason || "unknown error"]).map((err, i) => (
                  <Text key={i}>• {err}</Text>
                ))}
              </VStack>
            </Box>
          )}
        </Box>
      )}

      {estimate && (
        <Box fontSize="sm" bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.100">
          <Text fontWeight="medium" color="gray.700" mb={1}>Estimate</Text>
          <HStack gap={6} flexWrap="wrap" color="gray.700">
            <Text>Duration: ~{estimate.estimatedDurationSec}s</Text>
            {estimate.estimatedMemoryMb != null && <Text>Peak memory: ~{estimate.estimatedMemoryMb} MiB</Text>}
            {estimate.estimatedSizeGb != null && <Text>Output: ~{estimate.estimatedSizeGb} GB</Text>}
            {estimate.estimatedCost != null && <Text>Cost: {estimate.estimatedCost}</Text>}
          </HStack>
        </Box>
      )}
    </VStack>
  );
}
